import { Component, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ConfirmationService } from 'primeng/api';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { UsersService } from 'src/app/_services/admin/users.service';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { ErrorUtil } from 'src/app/_utilities/error';

@Component({
  selector: 'app-manage-setup-access',
  standalone: false,
  templateUrl: './manage-setup-access.component.html',
  styleUrl: './manage-setup-access.component.scss',
  providers: [ConfirmationService],
})
export class ManageSetupAccessComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  data: any;

  profile: any;
  orgSettings: any;
  currentPackage: any;
  addonFeature: any;

  billingUser: boolean = false;
  aiAccess: boolean = false;
  offlineEnable: boolean = false;
  backupOfflineEnable: boolean = false;
  isBillingEditable: boolean = false;
  saving: boolean = false;

  constructor(
    private languageTranslateService: LanguageTranslateService,
    public modalRef: BsModalRef,
    private userService: UsersService,
    private auth: AuthenticationService,
    private errorUtil: ErrorUtil,
    private confirmationService: ConfirmationService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLanguageData();
    this.initFromData();
  }

  private initFromData() {
    this.profile = this.data?.profile || this.data;
    this.orgSettings = this.data?.orgSettings;
    this.currentPackage = this.data?.currentPackage ?? localStorage.getItem('packageCode');
    this.addonFeature = this.data?.addonFeature || this.auth.getAddonFeatureLists();

    if (!this.profile) {
      return;
    }

    this.billingUser = !!this.profile.billingUser;
    this.offlineEnable = !!this.profile.offlineEnable;
    this.backupOfflineEnable = this.offlineEnable;
    this.aiAccess = !!(
      this.profile.dailyJobRundown ||
      this.profile.postJobSummary ||
      this.profile.taskDurationSuggestion ||
      this.profile.checklistGeneration
    );

    try {
      const loggedInUser = JSON.parse(this.auth.getProfileInfo());
      this.isBillingEditable = !!loggedInUser?.billingUser;
    } catch {
      this.isBillingEditable = false;
    }
  }

  get showBilling(): boolean {
    return this.currentPackage !== 'Old';
  }

  get showAi(): boolean {
    return this.addonFeature?.addonAI == 'AI';
  }

  get showOffline(): boolean {
    return !!this.orgSettings?.offlineEnable;
  }

  onSave() {
    if (this.saving || !this.profile?.id) {
      return;
    }

    if (!this.offlineEnable && this.offlineEnable !== this.backupOfflineEnable) {
      this.confirmationService.confirm({
        key: 'setupAccessOffline',
        header: this.translate?.setup?.users?.users?.messages?.disableOfflineMode || 'Disable Offline Mode',
        message:
          'Disabling Offline Mode may prevent unsynced activities from being uploaded. Users with pending sync items could lose track of those records once Offline Mode is turned off.',
        accept: () => this.saveSetupAccess(),
        reject: () => {
          this.offlineEnable = this.backupOfflineEnable;
        },
      });
      return;
    }

    this.saveSetupAccess();
  }

  private saveSetupAccess() {
    const userId = this.profile?.id;
    if (!userId) {
      return;
    }

    this.saving = true;
    this.push(
      this.userService.getUserProfile(userId).subscribe((latestProfile: any) => {
        const formData: any = {
          ...latestProfile,
          billingUser: this.showBilling ? this.billingUser : latestProfile.billingUser,
          offlineEnable: this.showOffline ? this.offlineEnable : latestProfile.offlineEnable,
        };

        if (this.showAi) {
          formData.dailyJobRundown = this.aiAccess;
          formData.postJobSummary = this.aiAccess;
          formData.taskDurationSuggestion = this.aiAccess;
          formData.checklistGeneration = this.aiAccess;
        }

        if (formData.geoFence?.id) {
          formData.geoFence = { id: formData.geoFence.id };
        } else {
          formData.geoFence = null;
        }

        if (formData.defaultDepot?.id != null) {
          formData.defaultDepot = { id: formData.defaultDepot.id };
        } else {
          formData.defaultDepot = null;
        }

        if (Array.isArray(formData.userDynamicSkillList)) {
          formData.userDynamicSkillList = formData.userDynamicSkillList.map((s: any) => {
            const copy = { ...s };
            delete copy.label;
            return copy;
          });
        }

        this.push(
          this.userService.editUserProfile(formData, userId).subscribe((res: any) => {
            this.saving = false;
            if (res?.status === 200 || res?.status === 201) {
              this.errorUtil.setErrorMessage(
                200,
                this.translate?.general.messages.updatedSuccessfully,
                null,
                'success',
                1000
              );
              this.userService.refreshList$.emit(res);
              this.modalRef.hide();
            } else {
              this.errorUtil.setErrorMessage(
                res?.status || 400,
                null,
                res?.error?.title || res?.error?.message || null,
                'error',
                3000
              );
            }
          })
        );
      })
    );
  }

  /********************************** Start Language **************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
        }
      })
    );
  }
  /********************************** End Language **************************/

  push(obs: any) {
    super.push(obs);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
